
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '@fontsource/exo-2';
import './StrategyStyles.css';
import './BattleUI.css'; // Import for ability tooltip styles
import OrderDesign from './OrderDesign';
import { useNavigate } from 'react-router-dom';
import CustomDropdown from './CustomDropdown';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import { useNotifications } from './notifications';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox } from './tutorial/index';


const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

// Get faction orb background color/gradient (matching CommandCenter implementation)
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};
  
  const combinedFactions = {
    'The Conspiracy': ['The Conspiracy', 'League of Shadows', 'Asrah', 'The Sentients'],
    'The Corrupted Core': ['The Corrupted Core', 'Exolot Dominion', 'Asrah', 'The Sentients'],
    'The Covenant': ['The Covenant', 'Ra\'s Children', 'League of Shadows', 'The Sentients'],
    'The Divine Syndicate': ['The Divine Syndicate', 'Ra\'s Children', 'Exolot Dominion', 'The Sentients'],
    'The Forsaken Pact': ['The Forsaken Pact', 'Remnants of Humanity', 'Asrah', 'The Sentients'],
    'The Hollow': ['The Hollow', 'Ra\'s Children', 'Asrah', 'The Sentients'],
    'The Iron Alliance': ['The Iron Alliance', 'Remnants of Humanity', 'Exolot Dominion', 'The Sentients'],
    'The Last Crusade': ['The Last Crusade', 'Ra\'s Children', 'Remnants of Humanity', 'The Sentients'],
    'The Shadow Consortium': ['The Shadow Consortium', 'League of Shadows', 'Exolot Dominion', 'The Sentients'],
    'The Silent Accord': ['The Silent Accord', 'Remnants of Humanity', 'League of Shadows', 'The Sentients']
    };
    
    const allFactions = [
    'Remnants of Humanity',
    'Exolot Dominion',
    'League of Shadows',
    'Ra\'s Children',
    'Asrah',
    'The Conspiracy',
    'The Corrupted Core',
    'The Covenant',
    'The Divine Syndicate',
    'The Forsaken Pact',
    'The Hollow',
    'The Iron Alliance',
    'The Last Crusade',
    'The Shadow Consortium',
    'The Silent Accord'
    ];
    
// Helper function to get rarity sort order
const getRarityOrder = (rarity) => {
  const rarityOrder = { 'common': 1, 'rare': 2, 'epic': 3, 'legendary': 4 };
  return rarityOrder[(rarity || 'common').toLowerCase()] || 0;
};

// Memoized order card so only the hovered (and previously hovered) card re-render on hover
// Display format: unassigned/total (e.g. 3/3 = 3 copies owned, 0 assigned; 1/3 = 3 owned, 2 assigned)
const StrategyOrderCard = React.memo(function StrategyOrderCard({
  order,
  isHovered,
  onHover,
  onHoverEnd,
  onOrderClickId,
  orderCount,
  displayOwnedCount,
  maxCount,
  canPurchase,
  isDuplicateMode,
  shouldGrayOut,
  ownedCount,
  isMobile,
  unassignedCount,
  totalCount
}) {
  return (
    <div
      onMouseEnter={() => onHover(order)}
      onMouseLeave={() => onHoverEnd(order)}
      onClick={(e) => {
        if (canPurchase && !isDuplicateMode) {
          e.stopPropagation();
          e.preventDefault();
          onOrderClickId(order.id);
          return;
        }
      }}
      style={{
        cursor: shouldGrayOut && ownedCount >= 1 && (orderCount >= maxCount) ? 'pointer' : (shouldGrayOut ? (canPurchase ? 'pointer' : 'not-allowed') : 'pointer'),
        position: 'relative',
        willChange: !isMobile ? 'transform' : 'auto',
      }}
      className={`strategy-order-card ${shouldGrayOut ? 'order-unowned' : ''} ${isMobile ? 'mobile-order' : ''}`}
      data-mobile-order={isMobile}
    >
      <div
        style={{
          filter: shouldGrayOut ? 'grayscale(100%)' : 'none',
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: canPurchase ? 10 : 1,
        }}
        onClick={(e) => {
          if (canPurchase && !isDuplicateMode) {
            e.stopPropagation();
            onOrderClickId(order.id);
          }
        }}
      >
        <OrderDesign
          order={order}
          count={orderCount}
          ownedCount={displayOwnedCount}
          minimal={false}
          lazyLoad={true}
          showOwnedCounter={true}
          maxCount={maxCount}
          rarity={order.rarity}
          onClick={(e) => {
            if (e) e.stopPropagation();
            onOrderClickId(order.id);
          }}
        />
      </div>
      {canPurchase && isHovered && (
        <div
          className="purchase-dollar-sign"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onOrderClickId(order.id);
          }}
          style={{
            pointerEvents: 'auto !important',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >$</div>
      )}
      {order.rarity && (
        <div
          style={{
            position: 'absolute',
            bottom: '15px',
            right: '15px',
            fontSize: '0.9em',
            fontWeight: 'bold',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: '2px 6px',
            borderRadius: '4px',
            color: (() => {
              const r = (order.rarity || '').toLowerCase();
              if (r === 'common') return '#808080';
              if (r === 'rare') return '#4169E1';
              if (r === 'epic') return '#9370DB';
              if (r === 'legendary') return '#FFD700';
              return '#FFFFFF';
            })(),
            textShadow: (() => {
              const r = (order.rarity || '').toLowerCase();
              if (r === 'common') return '0 0 5px rgba(128, 128, 128, 0.8), 0 0 10px rgba(128, 128, 128, 0.6)';
              if (r === 'rare') return '0 0 5px rgba(65, 105, 225, 0.8), 0 0 10px rgba(65, 105, 225, 0.6)';
              if (r === 'epic') return '0 0 5px rgba(147, 112, 219, 0.8), 0 0 10px rgba(147, 112, 219, 0.6)';
              if (r === 'legendary') return '0 0 5px rgba(255, 215, 0, 0.8), 0 0 10px rgba(255, 215, 0, 0.6), 0 0 15px rgba(255, 215, 0, 0.4)';
              return 'none';
            })(),
            zIndex: 10002,
            pointerEvents: 'none',
          }}
        >
          {unassignedCount}/{totalCount}
        </div>
      )}
    </div>
  );
});

function Strategy({ user, onLogout }) {
  const { t, i18n: i18nInstance } = useTranslation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  // Play strategy music
  useMusic('/music/orchestra_3.mp3');
  
  // Notification system
  const { showNotification, showConfirmation, NotificationComponent, ConfirmationComponent } = useNotifications();
  
  // Detect mobile viewport for responsive design - optimized for landscape mode
  // In landscape, check height (iPhone 12 Pro landscape: 844px × 390px)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      const height = window.innerHeight;
      // For landscape mobile: check height (landscape phones have small heights)
      const isMobileByHeight = height <= 768;
      
      // Also check matchMedia if available (using height for landscape)
      let matchesMediaQuery = false;
      if (window.matchMedia) {
        const mediaQuery = window.matchMedia('(max-height: 768px)');
        matchesMediaQuery = mediaQuery.matches;
      }
      
      // Use matchMedia if available, otherwise use height check
      const isMobileResult = window.matchMedia ? matchesMediaQuery : isMobileByHeight;
      
      console.log('🔍 Initial mobile detection (landscape mode):', { 
        width, 
        height,
        isMobileByHeight,
        matchesMediaQuery,
        finalResult: isMobileResult
      });
      return isMobileResult;
    }
    return false;
  });
  
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window === 'undefined') return;
      
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // For landscape mobile: check height (landscape phones have small heights)
      const isMobileByHeight = height <= 768;
      
      // Also check matchMedia if available (using height for landscape)
      let matchesMediaQuery = false;
      if (window.matchMedia) {
        const mediaQuery = window.matchMedia('(max-height: 768px)');
        matchesMediaQuery = mediaQuery.matches;
      }
      
      // Prefer matchMedia, fallback to height check
      const isMobileResult = window.matchMedia ? matchesMediaQuery : isMobileByHeight;
      
      console.log('🔍 Mobile check (landscape mode):', { 
        width, 
        height,
        isMobileByHeight,
        matchesMediaQuery,
        finalResult: isMobileResult
      });
      
      setIsMobile(isMobileResult);
    };
    
    // Check immediately
    checkMobile();
    
    // Use matchMedia listener if available (more reliable)
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(max-height: 768px)');
      
      const handleMediaChange = (e) => {
        console.log('🔍 Media query changed (height):', { matches: e.matches, height: window.innerHeight, width: window.innerWidth });
        setIsMobile(e.matches);
      };
      
      // Set initial state
      handleMediaChange(mediaQuery);
      
      // Listen for changes
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleMediaChange);
      } else {
        mediaQuery.addListener(handleMediaChange);
      }
      
      // Also listen to resize as backup
      window.addEventListener('resize', checkMobile);
      
      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleMediaChange);
        } else {
          mediaQuery.removeListener(handleMediaChange);
        }
        window.removeEventListener('resize', checkMobile);
      };
    } else {
      // Fallback: just use resize listener
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);
  
  // Sync with viewport scaling hook's mobile detection for consistency
  useEffect(() => {
    if (isContainerMobile !== isMobile) {
      setIsMobile(isContainerMobile);
    }
  }, [isContainerMobile]);
  
  // i18n is ready when language/resources exist; no per-render debug logging

	  const [searchQuery, setSearchQuery] = useState('');
	  const [orderCounts, setOrderCounts] = useState({});
      const [sidebarMessages, setSidebarMessages] = useState([]);
	  const [selectedFaction, setSelectedFaction] = useState('Remnants of Humanity');
      const [orders, setOrders] = useState([]);
      const [selectedCosts, setSelectedCosts] = useState([]);
      const [showSentients, setShowSentients] = useState(true);
      const [showOnlyOwned, setShowOnlyOwned] = useState(false);
  const [sortBy, setSortBy] = useState('cost'); // 'cost', 'rarity', 'name', 'type'
	  const [userStrategies, setUserStrategies] = useState([]);
	const [selectedStrategyId, setSelectedStrategyId] = useState('');
	  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [modalStrategyName, setModalStrategyName] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalIsPrivate, setModalIsPrivate] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);
  const [orderDuplicators, setOrderDuplicators] = useState({
    common: 0,
    rare: 0,
    epic: 0,
    legendary: 0
  });
  const [billyBucks, setBillyBucks] = useState(0);
  const [originalStrategyOrders, setOriginalStrategyOrders] = useState(null); // Track original orders for change detection
  const strategyJustLoadedRef = React.useRef(false); // Track if strategy was just loaded (no changes made yet) - use ref for immediate availability
  const navigate = useNavigate();
  const [hoveredOrderId, setHoveredOrderId] = useState(null);
  const lastHoveredRef = useRef(null); // Track last hovered to avoid unnecessary updates
  const soundThrottleRef = useRef(0); // Throttle sound effects
  // Single state for sidebar tooltip position to avoid multiple re-renders on hover
  const [sidebarTooltipRect, setSidebarTooltipRect] = useState(null); // { index, top, bottom, left }
  const [strategyValidation, setStrategyValidation] = useState({ valid: true, unownedOrders: [] });
  const strategyLoadedByOnChange = React.useRef(false);
  const sidebarRef = React.useRef(null);
  const [factionTranslations, setFactionTranslations] = useState({}); // Map: english_name -> translated_name
  const [missingOrders, setMissingOrders] = useState([]); // Orders in strategy but not in current orders array
  const [showTutorialStrategy, setShowTutorialStrategy] = useState(false);
  const [tutorialStrategyStep, setTutorialStrategyStep] = useState(1);
  const [seenTutorialMessages, setSeenTutorialMessages] = useState([]);

  // Fetch tutorial status and show strategy tutorial when not yet seen
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/tutorial/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) setSeenTutorialMessages(data.seenMessages || []);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && !seenTutorialMessages.includes('strategy')) {
      setTutorialStrategyStep(1);
      const timer = setTimeout(() => setShowTutorialStrategy(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, seenTutorialMessages]);

  // Stable hover handlers so memoized cards only re-render when isHovered changes (2 cards max)
  const handleCardHover = useCallback((order) => {
    if (lastHoveredRef.current !== order.id) {
      lastHoveredRef.current = order.id;
      setHoveredOrderId(order.id);
      const now = Date.now();
      if (now - soundThrottleRef.current > 100) {
        soundThrottleRef.current = now;
        playSoundEffect('/sounds/hoverover.mp3', 0.3);
      }
    }
  }, []);
  const handleCardHoverEnd = useCallback((order) => {
    if (lastHoveredRef.current === order.id) {
      lastHoveredRef.current = null;
      setHoveredOrderId(null);
    }
  }, []);

  const handleOrderClickRef = useRef(() => {});
  const handleOrderClickById = useCallback((orderId) => {
    const order = (orders || []).find(o => o.id === orderId);
    if (order) handleOrderClickRef.current(order);
  }, [orders]);

  useEffect(() => {
    const getOrderPrice = (o) => {
      const r = (o.rarity || 'common').toLowerCase();
      return { common: 3000, rare: 6000, epic: 10000, legendary: 30000 }[r] || 3000;
    };
    handleOrderClickRef.current = async (order) => {
      const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
      const uniquenessLimit = order.is_unique === true ? 1 : 3;
      const maxCount = order.availability === 'Created' ? uniquenessLimit : Math.min(ownedCount, uniquenessLimit);
      const currentCount = orderCounts[order.id] ?? 0;
      const isAtMaxCount = currentCount >= maxCount;
      const canPurchase = isAtMaxCount && order.availability !== 'Created' && ownedCount < uniquenessLimit;
      const orderPrice = getOrderPrice(order);
      const canAfford = billyBucks >= orderPrice;
      const isUnowned = (ownedCount === 0 || ownedCount === undefined) && order.availability !== 'Created';

      if (canPurchase && !isDuplicateMode) {
        if (!canAfford) {
          showNotification(t('bank.insufficientBucks') || 'Insufficient Billy Bucks', 'error');
          return;
        }
        try {
          playSoundEffect('/sounds/click.mp3');
          const response = await fetch(`${API_URL}/bank/purchase`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ orderId: order.id })
          });
          const data = await response.json();
          if (response.ok) {
            setBillyBucks(data.newBalance || 0);
            const ordersResponse = await fetch(`${API_URL}/orders?faction=${encodeURIComponent(selectedFaction)}&lang=${encodeURIComponent(i18nInstance.language || 'en-us')}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (ordersResponse.ok) {
              const ordersData = await ordersResponse.json();
              const updatedOrder = ordersData.find(o => o.id === order.id);
              if (updatedOrder) setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
            }
            showNotification(data.message || 'Order purchased successfully', 'success');
          } else {
            showNotification(data.error || 'Failed to purchase order', 'error');
          }
        } catch (err) {
          showNotification('Failed to purchase order', 'error');
        }
        return;
      }

      if (isDuplicateMode) {
        const rarity = (order.rarity || 'common').toLowerCase();
        const duplicatorCount = orderDuplicators[rarity] || 0;
        const isUnique = order.is_unique === true;
        const dupMaxCount = isUnique ? 1 : 3;
        const dupCurrentCount = orderCounts[order.id] ?? 0;
        if (duplicatorCount <= 0) {
          showNotification(t('strategy.duplicate.errors.noDuplicator', { rarity: rarity.charAt(0).toUpperCase() + rarity.slice(1) }), 'error');
          return;
        }
        if (ownedCount < 1) {
          showNotification(t('strategy.duplicate.errors.mustOwn'), 'error');
          return;
        }
        if (dupCurrentCount >= dupMaxCount) {
          showNotification(t('strategy.duplicate.errors.maxReached', { max: dupMaxCount }), 'error');
          return;
        }
        try {
          playSoundEffect('/sounds/click.mp3');
          const response = await fetch(`${API_URL}/purchase-duplicate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ orderId: order.id })
          });
          const data = await response.json();
          if (response.ok) {
            const duplicatorResponse = await fetch(`${API_URL}/profile/consumables`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if (duplicatorResponse.ok) {
              const duplicatorData = await duplicatorResponse.json();
              setOrderDuplicators(duplicatorData.duplicators || { common: 0, rare: 0, epic: 0, legendary: 0 });
              setBillyBucks(duplicatorData.billyBucks || 0);
            }
            const ordersResponse = await fetch(`${API_URL}/orders?faction=${encodeURIComponent(selectedFaction)}&lang=${encodeURIComponent(i18nInstance.language || 'en-us')}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if (ordersResponse.ok) {
              const ordersData = await ordersResponse.json();
              const updatedOrder = ordersData.find(o => o.id === order.id);
              if (updatedOrder) setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
            }
            playSoundEffect('/sounds/add_to_strat.mp3');
            setSidebarMessages(prev => {
              const updated = [...prev, { name: order.name, cost: order.cost, isOwned: true }];
              updated.sort((a, b) => a.cost - b.cost);
              return updated;
            });
            setOrderCounts(prev => ({ ...prev, [order.id]: (prev[order.id] || 0) + 1 }));
            strategyJustLoadedRef.current = false;
          } else {
            showNotification(data.error || t('strategy.duplicate.errors.purchaseFailed'), 'error');
          }
        } catch (err) {
          showNotification(t('strategy.duplicate.errors.purchaseFailed'), 'error');
        }
        return;
      }

      if (canPurchase) return;
      if (isUnowned) return;
      if (isAtMaxCount) return;
      playSoundEffect('/sounds/add_to_strat.mp3');
      setSidebarMessages(prev => {
        const updated = [...prev, { name: order.name, cost: order.cost, isOwned: true }];
        updated.sort((a, b) => a.cost - b.cost);
        return updated;
      });
      setOrderCounts(prev => ({ ...prev, [order.id]: currentCount + 1 }));
      strategyJustLoadedRef.current = false;
    };
  }, [orderCounts, orderDuplicators, billyBucks, selectedFaction, i18nInstance.language, showNotification, playSoundEffect, t, isDuplicateMode]);

  // Fetch duplicators and Billy Bucks on component mount
  useEffect(() => {
    const fetchDuplicators = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/profile/consumables`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setOrderDuplicators(data.duplicators || {
            common: 0,
            rare: 0,
            epic: 0,
            legendary: 0
          });
          setBillyBucks(data.billyBucks || 0);
        } else {
          console.error('Failed to fetch consumables:', response.status);
        }
      } catch (err) {
        console.error('Error fetching consumables:', err);
      }
    };
    
    fetchDuplicators();
  }, []);

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        // Fallback: use English names
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, [i18nInstance.language]);

  // Reset the flag when selectedStrategyId changes (but not when it's set by onChange)
  useEffect(() => {
    if (!selectedStrategyId) {
      strategyLoadedByOnChange.current = false;
    }
  }, [selectedStrategyId]);

  // Prevent sidebar scroll from propagating to main page
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleWheel = (e) => {
      // Check if sidebar is scrollable and if we're at the boundaries
      const { scrollTop, scrollHeight, clientHeight } = sidebar;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      const scrollingDown = e.deltaY > 0;
      const scrollingUp = e.deltaY < 0;

      // If scrolling down and at bottom, or scrolling up and at top, allow default (scroll main page)
      // Otherwise, prevent default (scroll sidebar only)
      if ((scrollingDown && isAtBottom) || (scrollingUp && isAtTop)) {
        // Allow scroll to propagate to main page only at boundaries
        return;
      }

      // Prevent scroll from propagating to main page
      e.stopPropagation();
      // Allow the sidebar to scroll naturally by not preventing default
    };

    sidebar.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      sidebar.removeEventListener('wheel', handleWheel);
    };
  }, []);



// ✅ Insert this block right below:
// Note: This useEffect handles the case where orders load after a strategy is selected
// The main loading logic is in the CustomDropdown onChange handler
useEffect(() => {
  // Skip if onChange handler is currently loading, or if no strategy selected
  if (!selectedStrategyId || strategyLoadedByOnChange.current || (sidebarMessages || []).length > 0) {
    return;
  }

  const selected = (userStrategies || []).find(s => s.id.toString() === selectedStrategyId);
  if (!selected || !Array.isArray(selected.orders)) return;

  // Only run when orders are loaded
  if (orders.length === 0) {
    return;
  }

  const newMessages = [];
  const newCounts = {};
  const missingOrderIds = [];

  selected.orders.forEach(({ orderId, order_id, count, isOwned, availability }) => {
    // Support both orderId (camelCase) and order_id (snake_case) for compatibility
    const id = orderId || order_id;
    if (!id) return;
    
    const order = (orders || []).find(o => o.id === id);
    if (order) {
      const owned = isOwned !== false || availability === 'Created';
      for (let i = 0; i < count; i++) {
        newMessages.push({ 
          name: order.name, 
          cost: order.cost,
          isOwned: owned
        });
      }
      newCounts[order.id] = count;
    } else {
      // Order not found in current orders array - mark as missing
      missingOrderIds.push({ id, count, isOwned, availability });
    }
  });

  // Fetch missing orders if any
  if (missingOrderIds.length > 0) {
    Promise.all(missingOrderIds.map(({ id }) =>
      fetch(`${API_URL}/order/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }).then(res => res.ok ? res.json() : null).catch(() => null)
    )).then(fetchedOrders => {
      // Store missing orders for grid display (only those not in current orders array)
      const validFetchedOrders = fetchedOrders.filter(o => {
        if (!o) return false;
        // Only include if not already in orders array
        return !(orders || []).find(existingOrder => existingOrder.id === o.id);
      });
      setMissingOrders(validFetchedOrders);
      
      // Add fetched orders to sidebar
      fetchedOrders.forEach((order, index) => {
        if (order) {
          const { count, isOwned, availability } = missingOrderIds[index];
          const owned = isOwned !== false || availability === 'Created';
          for (let i = 0; i < count; i++) {
            newMessages.push({ 
              name: order.name, 
              cost: order.cost,
              isOwned: owned
            });
          }
          newCounts[order.id] = count;
        }
      });
      
      if (newMessages.length > 0) {
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log("Sidebar messages to set (from useEffect - orders loaded after selection):", newMessages);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
      }
    });
  } else {
    // No missing orders, clear the state
    setMissingOrders([]);
    
    if (newMessages.length > 0) {
      // Sort by cost
      newMessages.sort((a, b) => a.cost - b.cost);
      console.log("Sidebar messages to set (from useEffect - orders loaded after selection):", newMessages);
      setSidebarMessages(newMessages);
      setOrderCounts(newCounts);
    }
  }
}, [orders, selectedStrategyId, userStrategies, (sidebarMessages || []).length]); // Include all dependencies

// Handle strategy loading after faction switch
const pendingStrategyLoadAfterFactionSwitch = React.useRef(null);
useEffect(() => {
  // If faction was switched and orders are now loaded, continue loading the strategy
  if (pendingStrategyLoadAfterFactionSwitch.current && orders.length > 0 && selectedStrategyId) {
    const { strategyId, selected } = pendingStrategyLoadAfterFactionSwitch.current;
    pendingStrategyLoadAfterFactionSwitch.current = null;
    
    console.log('🔵 Continuing strategy load after faction switch');
    // Re-trigger the onChange handler logic to complete the loading
    // We'll manually call the validation and loading logic
    strategyLoadedByOnChange.current = true;
    
    // Use a small delay to ensure orders are fully processed
    setTimeout(async () => {
      try {
        const validationRes = await fetch(`${API_URL}/strategy/${strategyId}/validate`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        const validation = await validationRes.json();
        setStrategyValidation(validation);
        
        const hasUnowned = selected.orders.some(({ orderId, order_id, isOwned, availability }) => {
          if (availability === 'Created') return false;
          return isOwned === false;
        });
        
        const newMessages = [];
        const newCounts = {};
        const missingOrderIds = [];
        
        selected.orders.forEach(({ orderId, order_id, count, isOwned, availability }) => {
          const id = orderId || order_id;
          if (!id) return;
          
          const order = (orders || []).find(o => o.id === id);
          if (order) {
            // Add ALL orders (owned and unowned) but mark unowned ones
            const owned = isOwned !== false || availability === 'Created';
            if (hasUnowned || !validation.valid) {
              // Include all orders when there are unowned ones
              for (let i = 0; i < count; i++) {
                newMessages.push({ 
                  name: order.name, 
                  cost: order.cost,
                  isOwned: owned
                });
              }
              newCounts[id] = count;
            } else {
              // All owned case
              for (let i = 0; i < count; i++) {
                newMessages.push({ 
                  name: order.name, 
                  cost: order.cost,
                  isOwned: true
                });
              }
              newCounts[id] = count;
            }
          } else {
            missingOrderIds.push({ id, count, isOwned, availability });
          }
        });
        
        // Fetch missing orders
        if (missingOrderIds.length > 0) {
          const fetchPromises = missingOrderIds.map(({ id }) =>
            fetch(`${API_URL}/order/${id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }).then(res => res.ok ? res.json() : null).catch(() => null)
          );
          
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display (only those not in current orders array)
          const validFetchedOrders = fetchedOrders.filter(o => {
            if (!o) return false;
            // Only include if not already in orders array
            return !(orders || []).find(existingOrder => existingOrder.id === o.id);
          });
          setMissingOrders(validFetchedOrders);
          
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count, isOwned, availability } = missingOrderIds[index];
              const owned = isOwned !== false || availability === 'Created';
              if (hasUnowned || !validation.valid) {
                // Include all orders when there are unowned ones
                for (let i = 0; i < count; i++) {
                  newMessages.push({ 
                    name: order.name, 
                    cost: order.cost,
                    isOwned: owned
                  });
                }
                newCounts[order.id] = count;
              } else {
                // All owned case
                for (let i = 0; i < count; i++) {
                  newMessages.push({ 
                    name: order.name, 
                    cost: order.cost,
                    isOwned: true
                  });
                }
                newCounts[order.id] = count;
              }
            }
          });
        } else {
          // No missing orders, clear the state
          setMissingOrders([]);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (after faction switch):', newMessages.length);
  setSidebarMessages(newMessages);
  setOrderCounts(newCounts);
        strategyLoadedByOnChange.current = false;
      } catch (err) {
        console.error('Failed to load strategy after faction switch:', err);
        strategyLoadedByOnChange.current = false;
      }
    }, 200);
  }
}, [orders, selectedStrategyId, userStrategies]);


      
useEffect(() => {
  const fetchOrdersAndStrategies = async () => {
    // --- Fetch Orders ---
    if (selectedFaction) {
      let factionsToFetch = combinedFactions[selectedFaction] || [selectedFaction];
      if (showSentients && !factionsToFetch.includes('The Sentients')) {
        factionsToFetch.push('The Sentients');
      }

      // Get current language from i18n and normalize it for the API
      const currentLanguage = i18nInstance.language || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      const token = localStorage.getItem('token');

      // Fetch all factions in parallel instead of sequentially for faster load
      const results = await Promise.all(
        factionsToFetch.map(async (faction) => {
          try {
            const response = await fetch(`${API_URL}/orders?faction=${encodeURIComponent(faction)}&lang=${encodeURIComponent(normalizedLanguage)}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const data = await response.json();
            return Array.isArray(data) ? data : [];
          } catch (err) {
            console.error(`Failed to fetch orders for ${faction}`);
            return [];
          }
        })
      );
      const allOrders = results.flat();

      const uniqueOrders = [];
      const seenIds = new Set();
      for (const order of allOrders) {
        if (!seenIds.has(order.id)) {
          seenIds.add(order.id);
          uniqueOrders.push(order);
        }
      }

      // Sort will be applied dynamically based on sortBy state
      // Initial sort by cost (default)
      uniqueOrders.sort((a, b) => {
        const normalize = name => (name ?? '').trim().toLowerCase();
        const aIsSentient = normalize(a.faction_name) === 'the sentients';
        const bIsSentient = normalize(b.faction_name) === 'the sentients';

        if (aIsSentient && !bIsSentient) return 1;
        if (!aIsSentient && bIsSentient) return -1;

        const costComparison = a.cost - b.cost;
        if (costComparison !== 0) return costComparison;

        return a.name.localeCompare(b.name);
      });

      setOrders(uniqueOrders);
    }

    // --- Fetch Strategies ---
   try {
  // Get current language from i18n and normalize it for the API
  const currentLanguage = i18nInstance.language || 'en-us';
  const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
  
  const response = await fetch(`${API_URL}/my-strategies?lang=${encodeURIComponent(normalizedLanguage)}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  });
  const data = await response.json();

  // Ensure it's an array
  if (Array.isArray(data)) {
    // Mark strategies with unowned orders
    const strategiesWithWarnings = data.map(strategy => {
      const hasUnowned = strategy.orders && strategy.orders.some(({ isOwned, availability }) => {
        if (availability === 'Created') return false;
        return isOwned === false;
      });
      return {
        ...strategy,
        hasUnownedOrders: hasUnowned
      };
    });
    setUserStrategies(strategiesWithWarnings);
  } else {
    console.error("Unexpected response format:", data);
    setUserStrategies([]);
  }
} catch (err) {
  console.error("Failed to fetch strategies:", err);
  setUserStrategies([]);
}

  };

  fetchOrdersAndStrategies();
}, [selectedFaction, showSentients, i18nInstance.language]);

  // Clear missing orders when faction changes (unless strategy is being loaded)
  useEffect(() => {
    if (!strategyLoadedByOnChange.current && !pendingStrategyLoadAfterFactionSwitch.current) {
      setMissingOrders([]);
    }
  }, [selectedFaction]);

  // Helper function to check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    // If we're currently loading a strategy, don't consider it as having unsaved changes
    if (strategyLoadedByOnChange.current || pendingStrategyLoadAfterFactionSwitch.current) {
      console.log('🔵 hasUnsavedChanges: Currently loading, returning false');
      return false;
    }
    
    // If strategy was just loaded and no changes have been made, don't consider it as having unsaved changes
    if (strategyJustLoadedRef.current) {
      console.log('🔵 hasUnsavedChanges: Strategy just loaded, returning false');
      return false;
    }
    
    if (!selectedStrategyId) {
      // If no strategy selected, check if there are any orders in sidebar
      const hasMessages = (sidebarMessages || []).length > 0;
      console.log('🔵 hasUnsavedChanges: No strategy selected, hasMessages:', hasMessages);
      return hasMessages;
    }
    
    // If we have a selectedStrategyId but no originalStrategyOrders yet, we're still loading
    // Don't consider this as having unsaved changes
    if (!originalStrategyOrders) {
      console.log('🔵 hasUnsavedChanges: Strategy selected but originalStrategyOrders not set yet (still loading), returning false');
      return false;
    }
    
    // If we have originalStrategyOrders but orderCounts is empty, we're still loading
    // Don't consider this as having unsaved changes
    if (Object.keys(orderCounts || {}).length === 0 && originalStrategyOrders.length > 0) {
      console.log('🔵 hasUnsavedChanges: Still loading (orderCounts empty), returning false');
      return false;
    }
    
    // Build original counts from originalStrategyOrders
    const originalCounts = {};
    originalStrategyOrders.forEach(({ orderId, order_id, count }) => {
      const id = String(orderId || order_id);
      if (id && id !== 'undefined' && id !== 'null') {
        originalCounts[id] = (originalCounts[id] || 0) + count;
      }
    });
    
    // Use orderCounts directly (more reliable than reconstructing from sidebarMessages)
    // Convert all keys to strings for consistent comparison
    const currentCounts = {};
    Object.keys(orderCounts || {}).forEach(key => {
      currentCounts[String(key)] = orderCounts[key];
    });
    
    // Check if counts match - compare both directions
    const allOrderIds = new Set([...Object.keys(currentCounts), ...Object.keys(originalCounts)]);
    let hasChanges = false;
    for (const orderId of allOrderIds) {
      const currentCount = currentCounts[orderId] || 0;
      const originalCount = originalCounts[orderId] || 0;
      if (currentCount !== originalCount) {
        console.log('🔵 hasUnsavedChanges: Mismatch found', { orderId, currentCount, originalCount });
        hasChanges = true;
        break;
      }
    }
    
    console.log('🔵 hasUnsavedChanges: Final result', {
      hasChanges,
      strategyJustLoaded: strategyJustLoadedRef.current,
      selectedStrategyId,
      originalOrdersCount: originalStrategyOrders?.length,
      currentOrdersCount: Object.keys(currentCounts).length,
      originalCounts,
      currentCounts
    });
    
    return hasChanges;
  }, [selectedStrategyId, originalStrategyOrders, sidebarMessages, orderCounts]);

  // Handle Esc key for navigation with unsaved changes warning
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle ESC key
      if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
        // Don't handle if:
        // 1. User is typing in an input/textarea
        // 2. A modal is open (modals should handle ESC themselves)
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        );

        const hasOpenModal = document.querySelector('.modal-overlay, [role="dialog"], .mulligan-dialog, .counterstrike-dialog, .friends-modal, .chat-modal');

        if (isInputFocused || hasOpenModal || showSaveDialog) {
          // Let the input or modal handle ESC first
          return;
        }

        // Check for unsaved changes
        if (hasUnsavedChanges()) {
          event.preventDefault();
          event.stopPropagation();
          showConfirmation(t('strategy.alerts.unsavedChanges'), (confirmed) => {
            if (confirmed) {
              // User confirmed, allow navigation
              playSoundEffect('/sounds/click.mp3');
              navigate('/welcome');
            }
          });
          return;
        }

        // Navigate back
        playSoundEffect('/sounds/click.mp3');
        navigate('/welcome');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, t, showSaveDialog, hasUnsavedChanges]);

  function handleOpenSaveDialog() {
    playSoundEffect('/sounds/click.mp3');
    
    // If a strategy is selected and there are changes, ask user
    if (selectedStrategyId && hasUnsavedChanges()) {
      showConfirmation(t('strategy.alerts.saveToExisting'), (saveToExisting) => {
        if (saveToExisting) {
          // Save to existing strategy
          handleSaveToExistingStrategy();
        } else {
          // User chose not to save to existing, continue with new strategy dialog
          setModalStrategyName('');
          setModalDescription('');
          setModalIsPrivate(false);
          setShowSaveDialog(true);
        }
      });
      return;
    }
    
    setModalStrategyName('');
    setModalDescription('');
    setModalIsPrivate(false);
    setShowSaveDialog(true);
  }

  function handleSaveToExistingStrategy() {
    const orderCount = (sidebarMessages || []).length;
    
    // Check if strategy has invalid order count and show warning
    if (orderCount < 50 || orderCount > 100) {
      let warningMessage = '';
      if (orderCount < 50) {
        warningMessage = t('strategy.alerts.minOrdersWarning');
      } else if (orderCount > 100) {
        warningMessage = t('strategy.alerts.maxOrdersWarning');
      }
      
      showConfirmation(warningMessage, (confirmed) => {
        if (!confirmed) {
          return; // User cancelled, don't save
        }
        // Continue with save
        continueSaveToExisting();
      });
      return;
    }
    
    continueSaveToExisting();
  }
  
  function continueSaveToExisting() {
    const orderCount = (sidebarMessages || []).length;
    const strategyOrders = [];
    const counts = {};
    (sidebarMessages || []).forEach(({ name, cost }) => {
      const order = (orders || []).find(o => o.name === name && o.cost === cost);
      if (order) {
        counts[order.id] = (counts[order.id] ?? 0) + 1;
      }
    });
    for (const [orderId, count] of Object.entries(counts)) {
      strategyOrders.push({ orderId: parseInt(orderId), count });
    }
    
    fetch(`${API_URL}/strategy/${selectedStrategyId}/orders`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({
        orders: strategyOrders
      })
    })
      .then(async res => {
        const data = await res.json();
        if (res.ok) {
          showNotification(data.message ?? t('strategy.alerts.updated'), 'success');
          // Update original orders to reflect saved state
          setOriginalStrategyOrders(strategyOrders.map(({ orderId, count }) => ({ orderId, count })));
          strategyJustLoadedRef.current = true; // Strategy saved, no unsaved changes now
        } else {
          showNotification(data.error || t('strategy.alerts.updateFailed'), 'error');
        }
      })
      .catch(err => {
        showNotification(t('strategy.alerts.updateFailed'), 'error');
      });
  }

  function handleSaveStrategy() {
    if (!modalStrategyName.trim()) {
      showNotification(t('strategy.alerts.noName'), 'warning');
      return;
    }
    
    const orderCount = (sidebarMessages || []).length;
    
    // Check if strategy has invalid order count and show warning
    if (orderCount < 50 || orderCount > 100) {
      let warningMessage = '';
      if (orderCount < 50) {
        warningMessage = t('strategy.alerts.minOrdersWarning');
      } else if (orderCount > 100) {
        warningMessage = t('strategy.alerts.maxOrdersWarning');
      }
      
      showConfirmation(warningMessage, (confirmed) => {
        if (!confirmed) {
          return; // User cancelled, don't save
        }
        // Continue with save
        continueSaveStrategy();
      });
      return;
    }
    
    continueSaveStrategy();
  }
  
  function continueSaveStrategy() {
    const orderCount = (sidebarMessages || []).length;
    const strategyOrders = [];
    const counts = {};
    (sidebarMessages || []).forEach(({ name, cost }) => {
      const order = (orders || []).find(o => o.name === name && o.cost === cost);
      if (order) {
        counts[order.id] = (counts[order.id] ?? 0) + 1;
      }
    });
    for (const [orderId, count] of Object.entries(counts)) {
      strategyOrders.push({ orderId: parseInt(orderId), count });
    }
    fetch(`${API_URL}/save-strategy`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({
        name: modalStrategyName,
        description: modalDescription,
        is_private: modalIsPrivate,
        orders: strategyOrders
      })
    })
      .then(res => res.json())
      .then(data => {
        showNotification(data.message ?? t('strategy.alerts.saved'), 'success');
        setShowSaveDialog(false);
        // Update original orders if this was saved as a new strategy
        setOriginalStrategyOrders(null);
        setSelectedStrategyId('');
        strategyJustLoadedRef.current = false; // New strategy, clear flag
        // Refresh strategies list
        fetch(`${API_URL}/my-strategies?lang=${encodeURIComponent(i18nInstance.language || 'en-us')}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        })
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) {
              setUserStrategies(data);
            }
          })
          .catch(err => console.error("Failed to refresh strategies:", err));
      })
      .catch(err => {
        showNotification(t('strategy.alerts.saveFailed'), 'error');
      });
  }   

function fuzzyMatch(text, query) {
  if (!query) return true;
  const normalizedText = (text || '').toLowerCase();
  const words = query.toLowerCase().split(/\s+/);
  return words.every(word => normalizedText.includes(word));
}

  
            // Debug: Log mobile state before rendering
            if (typeof window !== 'undefined') {
              console.log('🎨 Rendering Strategy:', { 
                isMobile, 
                windowWidth: window.innerWidth,
                hasMobileClass: isMobile ? 'YES' : 'NO'
              });
            }
            
            return (
            <div className="strategy-viewport-wrapper">
              {/* Full-screen background - outside viewport-container to cover entire screen */}
              <div 
                className="strategy-fullscreen-background"
                style={{
                  backgroundImage: 'url(/images/strategy.jpg)',
                }}
              />
              
              {/* Viewport container - scales to fit screen */}
              <div 
                className="strategy-viewport-container"
                style={{
                  width: `${containerBaseWidth}px`,
                  height: `${containerBaseHeight}px`,
                  transformOrigin: '0 0',
                  transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
                }}
              >
                <div 
                  className={`strategy-container${isMobile ? ' mobile' : ''}`}
                  data-width={typeof window !== 'undefined' ? window.innerWidth : 'unknown'}
                >
			{user && (
  <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
    <FriendsIcon />
    <SettingsMenu onLogout={() => {
      playSoundEffect('/sounds/click.mp3');
      onLogout();
      navigate('/');
    }} />
    <span style={{ color: 'white', marginRight: '10px' }}>{user}</span>
  </div>
			)}

<button onClick={() => {
  playSoundEffect('/sounds/click.mp3');
  if (hasUnsavedChanges()) {
    showConfirmation(t('strategy.alerts.unsavedChanges'), (confirmed) => {
      if (confirmed) {
        // User confirmed, allow navigation
        navigate('/welcome');
      }
    });
    return;
  }
  navigate('/welcome');
}} className="back-button">
  {t('common.back')}
</button>

            <div className="strategy-main">
              <img 
                src="/images/strategy.jpg" 
                alt="Background" 
                className="strategy-background-image"
              />
            {/* Fixed Header Section - stays at top, no scrolling through */}
            <div className="strategy-header-fixed">
            
            <div className="strategy-faction-bar">
            <div className="strategy-faction-icons">
  {allFactions.map(faction => {
  const isCombined = combinedFactions[faction];
  const color = isCombined ? factionColors['Combined'] : factionColors[faction] || '#ccc';
  const isSelected = selectedFaction === faction;

  let computedBackground = color;

  if (faction === 'The Conspiracy') {
    computedBackground = 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
  } else if (faction === 'The Corrupted Core') {
    computedBackground = 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
  } else if (faction === 'The Covenant') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
  } else if (faction === 'The Divine Syndicate') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
  } else if (faction === 'The Forsaken Pact') {
    computedBackground = 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
  } else if (faction === 'The Hollow') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
  } else if (faction === 'The Iron Alliance') {
    computedBackground = 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
  } else if (faction === 'The Last Crusade') {
    computedBackground = 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
  } else if (faction === 'The Shadow Consortium') {
    computedBackground = 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
  } else if (faction === 'The Silent Accord') {
    computedBackground = 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
  }

  const translatedFactionName = factionTranslations[faction] || faction;
  
  return (
    <div
      key={faction}
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setSelectedFaction(faction);
      }}
      title={translatedFactionName}
      className={`faction-icon ${isSelected ? 'selected' : ''}`}
      style={{ background: computedBackground }}
    />
  );
})}

                          </div>
                          
                          <div className="cost-graph-wrapper">
<div className="cost-bar-container">
  {(() => {
    // Calculate counts for all costs
    const costCounts = [...Array(13).keys()].map(i => 
      (sidebarMessages || []).filter(msg => msg.cost === i).length
    );
    // Find the maximum count to scale proportionally
    const maxCount = Math.max(...costCounts, 1); // at least 1 to avoid division by zero
    const maxBarHeight = 60; // max height in pixels
    
    return [...Array(13).keys()].map(i => {
      const cost = i;
      const count = costCounts[i];
      // Scale bar height proportionally to max count
      const barHeight = maxCount > 0 ? (count / maxCount) * maxBarHeight : 0;
      
      return (
        <div key={cost} className="cost-bar-segment">
          <div
            className="cost-bar-fill"
            style={{ height: `${barHeight}px` }}
            title={`Cost ${cost}: ${count} orders`}
          />
          <span className="cost-bar-label">{cost}</span>
        </div>
      );
    });
  })()}
</div>
<div className="cost-circle-container">
  {[...Array(13).keys()].map(i => {
    const cost = i + 0;
    const isActive = selectedCosts.includes(cost);
    return (
      <div
        key={cost}
        className={`cost-circle ${isActive ? 'active' : ''}`}
        onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          setSelectedCosts(prev =>
            prev.includes(cost) ? prev.filter(c => c !== cost) : [...prev, cost]
          );
        }}
      >
        {cost}
      </div>
    );
  })}
</div>
<div style={{ 
  marginTop: '8px', 
  color: 'white', 
  fontSize: '0.9em', 
  display: 'flex', 
  alignItems: 'center', 
  gap: '5px' 
}}>
  <img 
    src="/images/billy_bucks.png" 
    alt="Billy Bucks" 
    style={{ width: '20px', height: '20px' }}
  />
  <span>{billyBucks.toLocaleString()}</span>
</div>



        
                      </div>
                      </div>
                      
                      <div className="strategy-selected-faction">
                    {selectedFaction && (factionTranslations[selectedFaction] || selectedFaction)}
                    </div>
   
<div style={{ margin: '8px 0 5px 0', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
  <input
    type="text"
    className="strategy-picklist"
    placeholder={t('strategy.searchPlaceholder')}
    value={searchQuery}
    onChange={e => setSearchQuery(e.target.value)}
    style={{ flex: '1', fontSize: '0.9em', padding: '6px', maxWidth: '300px' }}
  />
  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'white', fontSize: '0.9em', whiteSpace: 'nowrap' }}>
    {t('strategy.sortBy') || 'Sort by:'}
    <div style={{ minWidth: '150px', maxWidth: '150px' }}>
      <CustomDropdown
        options={[
          { value: 'cost', label: t('strategy.sort.cost') || 'Cost' },
          { value: 'rarity', label: t('strategy.sort.rarity') || 'Rarity' },
          { value: 'name', label: t('strategy.sort.name') || 'Name' },
          { value: 'type', label: t('strategy.sort.type') || 'Type' }
        ]}
        value={sortBy}
        onChange={(value) => {
          playSoundEffect('/sounds/click.mp3');
          setSortBy(value);
        }}
        placeholder={t('strategy.sortBy') || 'Sort by:'}
      />
    </div>
  </label>
  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'white', fontSize: '0.9em', cursor: 'pointer', whiteSpace: 'nowrap' }}>
    <input
      type="checkbox"
      checked={showOnlyOwned}
      onChange={e => {
        playSoundEffect('/sounds/click.mp3');
        setShowOnlyOwned(e.target.checked);
      }}
      style={{ cursor: 'pointer' }}
    />
    {t('strategy.onlyShowOwned')}
  </label>
  {isDuplicateMode ? (
    <button
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setIsDuplicateMode(false);
      }}
      style={{
        padding: '6px 12px',
        fontSize: '0.9em',
        backgroundColor: '#ff6b6b',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '4px',
        whiteSpace: 'nowrap'
      }}
    >
      {t('strategy.duplicate.cancel')}
    </button>
  ) : (
    <button
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setIsDuplicateMode(true);
      }}
      style={{
        padding: '6px 12px',
        fontSize: '0.9em',
        backgroundColor: '#666',
        color: 'white',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '4px',
        whiteSpace: 'nowrap'
      }}
    >
      {t('strategy.duplicate.button')}
    </button>
  )}
  {isDuplicateMode && (
    <div style={{ color: 'white', fontSize: '0.9em', fontStyle: 'italic', width: '100%', marginTop: '5px' }}>
      {t('strategy.duplicate.instruction')}
    </div>
  )}
</div>
</div>
{/* End Fixed Header Section */}

            {/* Scrollable Content Area */}
            <div className="strategy-content-scrollable">
 <div className="strategy-orders-grid">
  
{useMemo(() => (orders || [])
    .filter(order => selectedCosts.length === 0 || selectedCosts.includes(order.cost))
    .filter(order => order.availability !== 'Created')
    .filter(order => {
      // Duplicate mode filter: show only orders where user has duplicators and does not have maximum amount
      if (isDuplicateMode) {
        // Filter 1: Show only orders where you have duplicators
        const rarity = (order.rarity || 'common').toLowerCase();
        const duplicatorCount = orderDuplicators[rarity] || 0;
        const hasDuplicator = duplicatorCount > 0;
        
        if (!hasDuplicator) {
          return false;
        }
        
        // Filter 2: Show only orders where you do NOT have the maximum amount owned
        const maxCount = order.is_unique === true ? 1 : 3;
        const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
        
        // Must own at least 1 copy to duplicate (required by backend)
        if (ownedCount < 1) {
          return false;
        }
        
        // Must have less than maximum amount owned
        if (ownedCount >= maxCount) {
          return false;
        }
        
        return true;
      }
      
      // Normal mode: apply other filters
      // Filter by ownership if checkbox is enabled
      if (showOnlyOwned) {
        const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
        const isOwned = ownedCount > 0 || order.availability === 'Created';
        if (!isOwned) return false;
      }
      return true;
    })
    .filter(order =>
      fuzzyMatch(order.name, searchQuery) ||
      fuzzyMatch(order.type, searchQuery) ||
      fuzzyMatch(order.description, searchQuery)
    )
    .sort((a, b) => {
      const normalize = name => (name ?? '').trim().toLowerCase();
      const aIsSentient = normalize(a.faction_name) === 'the sentients';
      const bIsSentient = normalize(b.faction_name) === 'the sentients';

      // Always put Sentients last
      if (aIsSentient && !bIsSentient) return 1;
      if (!aIsSentient && bIsSentient) return -1;

      // Primary sort by selected option
      let primaryComparison = 0;
      switch (sortBy) {
        case 'cost':
          primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
          break;
        case 'rarity':
          primaryComparison = getRarityOrder(a.rarity) - getRarityOrder(b.rarity);
          break;
        case 'name':
          primaryComparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'type':
          primaryComparison = (a.type || '').localeCompare(b.type || '');
          break;
        default:
          primaryComparison = (a.cost ?? 0) - (b.cost ?? 0);
      }

      if (primaryComparison !== 0) return primaryComparison;

      // Secondary sort by name (except when already sorting by name)
      if (sortBy !== 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }

      // If sorting by name and names are equal, sort by cost
      return (a.cost ?? 0) - (b.cost ?? 0);
    }), [orders, selectedCosts, isDuplicateMode, orderDuplicators, showOnlyOwned, searchQuery, sortBy])
    .map(order => {

      const color = combinedFactions[order.faction_name]
        ? factionColors['Combined']
        : factionColors[order.faction_name] || '#FFFFFF';

      // Use ownedCount if available, fallback to isOwned for backward compatibility
      const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
      const isUnowned = (ownedCount === 0 || ownedCount === undefined) && order.availability !== 'Created';
      
      // Calculate max count: limit by both ownership and uniqueness
      // If availability is 'Created', player has unlimited (use uniqueness limit)
      // Otherwise, limit by ownedCount (capped by uniqueness limit)
      const uniquenessLimit = order.is_unique === true ? 1 : 3;
      const maxCount = order.availability === 'Created' 
        ? uniquenessLimit  // Created orders: can add up to uniqueness limit
        : Math.min(ownedCount, uniquenessLimit);  // Owned orders: can only add as many as owned (capped by uniqueness)
      const currentCount = orderCounts[order.id] ?? 0;
      const isAtMaxCount = currentCount >= maxCount;
      
      // Gray out if unowned OR if at max count
      const shouldGrayOut = isUnowned || isAtMaxCount;
      
      // For display: use actual ownedCount (capped at maxCount for display purposes)
      // If availability is 'Created', show maxCount/maxCount (player has unlimited)
      const displayOwnedCount = order.availability === 'Created' ? maxCount : Math.min(ownedCount, maxCount);
      
      // Display format: unassigned/total
      // First number: how many copies are currently unassigned (not in strategy sidebar)
      // Second number: how many you own in total
      const unassignedCount = order.availability === 'Created'
        ? Math.max(0, maxCount - currentCount)
        : Math.max(0, ownedCount - currentCount);
      const totalCount = order.availability === 'Created' ? maxCount : ownedCount;
      
      // Check if order can be purchased:
      // 1. Order must be greyed out because it's at max count in strategy (isAtMaxCount)
      // 2. User doesn't own enough copies to add more (ownedCount < uniquenessLimit)
      // 3. Order is not 'Created' (which means unlimited)
      // Note: We check isAtMaxCount (not shouldGrayOut) because we only want to purchase when
      // the order is greyed out due to being at max in strategy, not because it's unowned
      const canPurchase = isAtMaxCount && order.availability !== 'Created' && ownedCount < uniquenessLimit;
      
      return (
        <StrategyOrderCard
          key={order.id}
          order={order}
          isHovered={hoveredOrderId === order.id}
          onHover={handleCardHover}
          onHoverEnd={handleCardHoverEnd}
          onOrderClickId={handleOrderClickById}
          orderCount={currentCount}
          displayOwnedCount={displayOwnedCount}
          maxCount={maxCount}
          canPurchase={canPurchase}
          isDuplicateMode={isDuplicateMode}
          shouldGrayOut={shouldGrayOut}
          ownedCount={ownedCount}
          isMobile={isMobile}
          unassignedCount={unassignedCount}
          totalCount={totalCount}
        />
      );
    })}
  
{/* Display missing orders from strategy (orders not in current faction or insufficiently owned) */}
{(missingOrders || [])
    .filter(order => {
      // Don't show if already in regular grid (to avoid duplicates)
      const isInRegularGrid = (orders || []).some(o => o.id === order.id);
      return !isInRegularGrid;
    })
    .filter(order => selectedCosts.length === 0 || selectedCosts.includes(order.cost))
    .filter(order =>
      fuzzyMatch(order.name, searchQuery) ||
      fuzzyMatch(order.type, searchQuery) ||
      fuzzyMatch(order.description, searchQuery)
    )
    .map(order => {
      const color = combinedFactions[order.faction_name]
        ? factionColors['Combined']
        : factionColors[order.faction_name] || '#FFFFFF';
      
      const currentCount = orderCounts[order.id] ?? 0;
      const isMissing = true; // These are missing orders
      
      return (
        <div
          key={`missing-${order.id}`}
          onMouseEnter={() => {
            const missingId = `missing-${order.id}`;
            const now = Date.now();
            const timeSinceLastSound = now - soundThrottleRef.current;
            
            if (lastHoveredRef.current !== missingId) {
              lastHoveredRef.current = missingId;
              setHoveredOrderId(missingId);
              
              // Throttle sound: only play if 100ms have passed since last sound
              if (timeSinceLastSound > 100) {
                soundThrottleRef.current = now;
                playSoundEffect('/sounds/hoverover.mp3', 0.3);
              }
            }
          }}
          onMouseLeave={() => {
            const missingId = `missing-${order.id}`;
            if (lastHoveredRef.current === missingId) {
              lastHoveredRef.current = null;
              setHoveredOrderId(null);
            }
          }}
          className={`strategy-order-card ${!isMobile && hoveredOrderId === `missing-${order.id}` ? 'strategy-order-hovered' : ''}`}
          style={{
            willChange: !isMobile ? 'transform' : 'auto',
            cursor: 'not-allowed',
            position: 'relative'
          }}
          className={`order-unowned ${isMobile ? 'mobile-order' : ''}`}
          data-mobile-order={isMobile}
          title="This order is in your strategy but either not available in the current faction or you don't own enough copies"
        >
          <div style={{
            filter: 'grayscale(100%)',
            pointerEvents: 'auto',
            position: 'relative'
          }}>
            <OrderDesign
              order={order}
              count={currentCount}
              ownedCount={0}
              minimal={false}
              lazyLoad={true}
              showOwnedCounter={false}
              maxCount={0}
              rarity={order.rarity}
              onClick={() => {
                // Prevent clicking missing orders
                showNotification('This order is not available in the current faction', 'warning');
              }}
            />
          </div>
          {/* Warning symbol */}
          <span style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            color: 'orange',
            fontSize: '1.5em',
            fontWeight: 'bold',
            filter: 'none',
            zIndex: 10003,
            textShadow: '0 0 5px rgba(255, 165, 0, 0.8), 0 0 10px rgba(255, 165, 0, 0.6)'
          }}>⚠️</span>
        </div>
      );
    })}
</div>
</div>
{/* End Scrollable Content Area */}

    </div>

<div className="strategy-sidebar" ref={sidebarRef}>

<div style={{ marginBottom: "10px" }}>

<div style={{ marginTop: "10px" }}>
  {selectedStrategyId && !strategyValidation.valid && (
    <>
      <span style={{ color: 'orange', marginLeft: '10px' }} title={`This strategy contains orders you don't own: ${(strategyValidation.unownedOrders || []).map(o => o.name).join(', ')}`}>
        ⚠️
      </span>
      <div style={{ color: 'orange', fontSize: '0.9em', marginTop: '5px', marginBottom: '5px' }}>
        This strategy contains orders you don't own. Please remove them before playing.
      </div>
    </>
  )}
<CustomDropdown
  options={(userStrategies || []).map(s => ({ 
    value: s.id.toString(), 
    label: s.name,
    hasWarning: s.hasUnownedOrders,
    faction_name: s.faction_name
  }))}
  value={selectedStrategyId}
  placeholder={t('strategy.selectStrategy')}
  renderOption={(option) => {
    const strategy = (userStrategies || []).find(s => s.id.toString() === option.value);
    const factionName = strategy?.faction_name || option.faction_name;
    if (!factionName) {
      return <span>{option.label} {option.hasWarning ? '⚠️' : ''}</span>;
    }
    const translatedFactionName = strategy?.faction_name_translated || factionTranslations[factionName] || factionName;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          className="faction-orb"
          style={{ background: getFactionOrbBackground(factionName) }}
          title={translatedFactionName}
        />
        <span>{option.label} {option.hasWarning ? '⚠️' : ''}</span>
      </div>
    );
  }}
  onChange={async (strategyId) => {
    console.log('🔵 CustomDropdown onChange called with strategyId:', strategyId);
    // Set flag FIRST before any state updates to prevent useEffect from interfering
    strategyLoadedByOnChange.current = true;
    
    setSelectedStrategyId(strategyId);
    setSidebarMessages([]);
    setOrderCounts({});
    setStrategyValidation({ valid: true, unownedOrders: [] }); // Reset validation
    setOriginalStrategyOrders(null); // Reset original orders
    setMissingOrders([]); // Clear missing orders
        strategyJustLoadedRef.current = false; // Reset flag when changing strategy
    
    if (!strategyId) {
      console.log('🔵 No strategyId, resetting');
      strategyLoadedByOnChange.current = false;
      return; // If deselecting, just reset
    }
    
    const selected = userStrategies.find(s => s.id.toString() === strategyId);
    console.log('🔵 Found strategy:', selected ? selected.name : 'NOT FOUND');
    if (!selected || !Array.isArray(selected.orders)) {
      console.log('🔵 Strategy not found or no orders array');
      strategyLoadedByOnChange.current = false; // Reset if we can't load
      return;
    }
    
    console.log('🔵 Strategy has', selected.orders.length, 'orders');
    console.log('🔵 Available orders count:', orders.length);
    
    // Determine the primary faction(s) from the strategy's orders
    // First, try to get faction_name from orders (might need to fetch if not available)
    const strategyFactions = new Set();
    const fetchedOrdersForFaction = [];
    
    // Collect faction names from orders that are already loaded
    selected.orders.forEach(({ orderId, order_id }) => {
      const id = orderId || order_id;
      if (!id) return;
      
      const order = (orders || []).find(o => o.id === id);
      if (order && order.faction_name) {
        strategyFactions.add(order.faction_name);
      } else {
        // Need to fetch this order to get its faction
        fetchedOrdersForFaction.push(id);
      }
    });
    
    // Fetch missing orders to determine their factions
    if (fetchedOrdersForFaction.length > 0) {
      console.log('🔵 Fetching', fetchedOrdersForFaction.length, 'orders to determine factions');
      const fetchPromises = fetchedOrdersForFaction.map(id =>
        fetch(`${API_URL}/order/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }).then(res => res.ok ? res.json() : null).catch(() => null)
      );
      
      const fetchedOrders = await Promise.all(fetchPromises);
      fetchedOrders.forEach(order => {
        if (order && order.faction_name) {
          strategyFactions.add(order.faction_name);
        }
      });
    }
    
    // Determine which faction to select
    const factionArray = Array.from(strategyFactions);
    console.log('🔵 Strategy uses factions:', factionArray);
    
    if (factionArray.length > 0) {
      // First, count all factions (including from fetched orders) to get accurate counts
      const factionCounts = {};
      
      // Count from already loaded orders
      selected.orders.forEach(({ orderId, order_id }) => {
        const id = orderId || order_id;
        if (!id) return;
        
        const order = (orders || []).find(o => o.id === id);
        if (order && order.faction_name) {
          factionCounts[order.faction_name] = (factionCounts[order.faction_name] || 0) + 1;
        }
      });
      
      // Also count from fetched orders (for faction detection)
      if (fetchedOrdersForFaction.length > 0) {
        const fetchPromises = fetchedOrdersForFaction.map(id =>
          fetch(`${API_URL}/order/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }).then(res => res.ok ? res.json() : null).catch(() => null)
        );
        
        const fetchedOrders = await Promise.all(fetchPromises);
        fetchedOrders.forEach(order => {
          if (order && order.faction_name) {
            factionCounts[order.faction_name] = (factionCounts[order.faction_name] || 0) + 1;
            // Also add to strategyFactions set
            strategyFactions.add(order.faction_name);
          }
        });
      }
      
      // Update factionArray with all factions (including from fetched orders)
      const allStrategyFactions = Array.from(strategyFactions);
      console.log('🔵 All strategy factions (after fetching):', allStrategyFactions);
      console.log('🔵 Faction counts:', factionCounts);
      
      // Check if it matches a combined faction
      // Combined factions have: [combinedName, baseFaction1, baseFaction2, 'The Sentients']
      // We need to match the base factions (excluding Sentients and the combined name itself)
      let matchedFaction = null;
      let bestMatch = null;
      let bestMatchScore = 0;
      
      for (const [combinedFaction, componentFactions] of Object.entries(combinedFactions)) {
        // Get base factions (exclude the combined faction name and Sentients)
        const baseFactions = componentFactions.filter(f => 
          f !== combinedFaction && f !== 'The Sentients'
        );
        
        // Get strategy's base factions (exclude Sentients)
        const strategyBaseFactions = allStrategyFactions.filter(f => f !== 'The Sentients');
        
        // Check if strategy has all the base factions of this combined faction
        const strategyBaseSet = new Set(strategyBaseFactions);
        const baseSet = new Set(baseFactions);
        
        // Match if strategy has all base factions
        const hasAllBaseFactions = baseFactions.every(f => strategyBaseSet.has(f));
        
        if (hasAllBaseFactions) {
          // Calculate match score: how many base factions match, and how many extra factions there are
          const matchingFactions = strategyBaseFactions.filter(f => baseSet.has(f)).length;
          const extraFactions = strategyBaseFactions.filter(f => !baseSet.has(f)).length;
          
          // Prefer exact matches (no extra factions), but also accept matches with extra factions
          // Score = matching factions - (extra factions * 0.5)
          const score = matchingFactions - (extraFactions * 0.5);
          
          console.log('🔵 Combined faction candidate:', combinedFaction, 
            'base factions:', baseFactions,
            'strategy has:', strategyBaseFactions,
            'score:', score);
          
          // Prefer exact match first
          if (extraFactions === 0 && matchingFactions === baseFactions.length) {
            matchedFaction = combinedFaction;
            console.log('🔵 Exact match found:', combinedFaction);
            break;
          }
          
          // Track best match if no exact match yet
          if (score > bestMatchScore) {
            bestMatchScore = score;
            bestMatch = combinedFaction;
          }
        }
      }
      
      // If we found a best match but no exact match, use the best match
      if (!matchedFaction && bestMatch) {
        matchedFaction = bestMatch;
        console.log('🔵 Using best match combined faction:', bestMatch, 'with score:', bestMatchScore);
      }
      
      // If no combined faction match, use the most common faction (excluding Sentients)
      if (!matchedFaction) {
        const nonSentientCounts = {};
        for (const [faction, count] of Object.entries(factionCounts)) {
          if (faction !== 'The Sentients') {
            nonSentientCounts[faction] = count;
          }
        }
        
        // Find the most common faction
        let maxCount = 0;
        for (const [faction, count] of Object.entries(nonSentientCounts)) {
          if (count > maxCount) {
            maxCount = count;
            matchedFaction = faction;
          }
        }
        
        // Fallback to first non-Sentient faction
        if (!matchedFaction) {
          matchedFaction = allStrategyFactions.find(f => f !== 'The Sentients') || allStrategyFactions[0];
        }
        
        console.log('🔵 Using most common faction:', matchedFaction);
      }
      
      if (matchedFaction && matchedFaction !== selectedFaction) {
        console.log('🔵 Switching faction from', selectedFaction, 'to', matchedFaction);
        // Store the strategy info to continue loading after faction switch
        pendingStrategyLoadAfterFactionSwitch.current = { strategyId, selected };
        setSelectedFaction(matchedFaction);
        // The useEffect will reload orders when selectedFaction changes
        // Then the pendingStrategyLoadAfterFactionSwitch useEffect will continue loading
        strategyLoadedByOnChange.current = false;
        return; // Exit early, will continue after orders reload
      }
    }
    
    // If orders aren't loaded yet, the useEffect will handle loading when they arrive
    if (orders.length === 0) {
      console.log('🔵 Orders not loaded yet, useEffect will handle loading');
      strategyLoadedByOnChange.current = false; // Let useEffect handle it
      return;
    }
    
    // Validate strategy ownership
    try {
      const validationRes = await fetch(`${API_URL}/strategy/${strategyId}/validate`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const validation = await validationRes.json();
      setStrategyValidation(validation);
      
      // Check if strategy has unowned orders
      // Support both orderId (camelCase) and order_id (snake_case)
      const hasUnowned = selected.orders.some(({ orderId, order_id, isOwned, availability }) => {
        if (availability === 'Created') return false;
        return isOwned === false;
      });
      
      if (hasUnowned || !validation.valid) {
        // Load ALL orders including unowned ones, mark them for visual distinction
        const newMessages = [];
        const newCounts = {};
        const missingOrderIds = [];
        
        // First pass: collect orders that are already loaded and identify missing ones
        selected.orders.forEach(({ orderId, order_id, count, isOwned, availability }) => {
          // Support both orderId (camelCase) and order_id (snake_case)
          const id = orderId || order_id;
          if (!id) {
            console.log('🔵 Order missing ID:', { orderId, order_id, count });
            return;
          }
          
          const order = (orders || []).find(o => o.id === id);
          if (order) {
            // Add ALL orders (owned and unowned) but mark unowned ones
            const owned = isOwned !== false || availability === 'Created';
            for (let i = 0; i < count; i++) {
              newMessages.push({ 
                name: order.name, 
                cost: order.cost,
                isOwned: owned // Mark whether this order is owned
              });
            }
            newCounts[id] = count;
          } else {
            // Fetch ALL missing orders (owned and unowned)
            missingOrderIds.push({ id, count, isOwned, availability });
          }
        });
        
        // Fetch missing orders
        if (missingOrderIds.length > 0) {
          console.log('🔵 Fetching', missingOrderIds.length, 'missing orders (with unowned)');
          const fetchPromises = missingOrderIds.map(({ id }) =>
            fetch(`${API_URL}/order/${id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }).then(res => res.ok ? res.json() : null).catch(err => {
              console.error(`Failed to fetch order ${id}:`, err);
              return null;
            })
          );
          
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display (only those not in current orders array)
          const validFetchedOrders = fetchedOrders.filter(o => {
            if (!o) return false;
            // Only include if not already in orders array
            return !(orders || []).find(existingOrder => existingOrder.id === o.id);
          });
          
          // Also include orders from validation that user doesn't own enough of (even if in current orders array)
          const insufficientOrderIds = new Set((validation.unownedOrders || validation.insufficientOrders || []).map(o => o.id));
          const insufficientOrdersInGrid = (orders || []).filter(o => insufficientOrderIds.has(o.id));
          
          // Combine both types of missing orders
          const allMissingOrders = [...validFetchedOrders, ...insufficientOrdersInGrid];
          // Remove duplicates
          const uniqueMissingOrders = Array.from(new Map(allMissingOrders.map(o => [o.id, o])).values());
          setMissingOrders(uniqueMissingOrders);
          
          // Add fetched orders to sidebar (both owned and unowned)
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count, isOwned, availability } = missingOrderIds[index];
              const owned = isOwned !== false || availability === 'Created';
              for (let i = 0; i < count; i++) {
                newMessages.push({ 
                  name: order.name, 
                  cost: order.cost,
                  isOwned: owned // Mark whether this order is owned
                });
              }
              newCounts[order.id] = count;
            }
          });
        } else {
          // Check if there are insufficient orders from validation (even if all orders are in current array)
          const insufficientOrderIds = new Set((validation.unownedOrders || validation.insufficientOrders || []).map(o => o.id));
          const insufficientOrdersInGrid = (orders || []).filter(o => insufficientOrderIds.has(o.id));
          setMissingOrders(insufficientOrdersInGrid);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (with unowned):', newMessages.length);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
        // Store original orders for change detection
        setOriginalStrategyOrders(selected.orders.map(({ orderId, order_id, count }) => ({
          orderId: orderId || order_id,
          count
        })));
        strategyJustLoadedRef.current = true; // Mark that strategy was just loaded (no changes yet)
        strategyLoadedByOnChange.current = false; // Reset flag after loading
      } else {
        // All orders are owned, load normally
        const newMessages = [];
        const newCounts = {};
        const missingOrderIds = [];
        
        // First pass: collect orders that are already loaded and identify missing ones
        selected.orders.forEach(({ orderId, order_id, count }) => {
          // Support both orderId (camelCase) and order_id (snake_case)
          const id = orderId || order_id;
          if (!id) {
            console.log('🔵 Order missing ID:', { orderId, order_id, count });
            return;
          }
          
          const order = (orders || []).find(o => o.id === id);
          if (order) {
            for (let i = 0; i < count; i++) {
              newMessages.push({ 
                name: order.name, 
                cost: order.cost,
                isOwned: true // All orders are owned in this branch
              });
            }
            newCounts[id] = count;
          } else {
            missingOrderIds.push({ id, count });
          }
        });
        
        // Fetch missing orders
        if (missingOrderIds.length > 0) {
          console.log('🔵 Fetching', missingOrderIds.length, 'missing orders');
          const fetchPromises = missingOrderIds.map(({ id }) =>
            fetch(`${API_URL}/order/${id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }).then(res => res.ok ? res.json() : null).catch(err => {
              console.error(`Failed to fetch order ${id}:`, err);
              return null;
            })
          );
          
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display (only those not in current orders array)
          const validFetchedOrders = fetchedOrders.filter(o => {
            if (!o) return false;
            // Only include if not already in orders array
            return !(orders || []).find(existingOrder => existingOrder.id === o.id);
          });
          setMissingOrders(validFetchedOrders);
          
          // Add fetched orders to sidebar
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count } = missingOrderIds[index];
              for (let i = 0; i < count; i++) {
                newMessages.push({ 
                  name: order.name, 
                  cost: order.cost,
                  isOwned: true // All orders are owned in this branch
                });
              }
              newCounts[order.id] = count;
            }
          });
        } else {
          // No missing orders, clear the state
          setMissingOrders([]);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (all owned):', newMessages.length);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
        // Store original orders for change detection
        setOriginalStrategyOrders(selected.orders.map(({ orderId, order_id, count }) => ({
          orderId: orderId || order_id,
          count
        })));
        strategyJustLoadedRef.current = true; // Mark that strategy was just loaded (no changes yet)
        strategyLoadedByOnChange.current = false; // Reset flag after loading
      }
    } catch (err) {
      console.error('Failed to validate strategy:', err);
      // Fallback to loading without validation
      const newMessages = [];
      const newCounts = {};
      const missingOrderIds = [];
      
      // First pass: collect orders that are already loaded and identify missing ones
      selected.orders.forEach(({ orderId, order_id, count }) => {
        // Support both orderId (camelCase) and order_id (snake_case)
        const id = orderId || order_id;
        if (!id) {
          console.log('🔵 Order missing ID (fallback):', { orderId, order_id, count });
          return;
        }
        
        const order = (orders || []).find(o => o.id === id);
        if (order) {
          for (let i = 0; i < count; i++) {
            newMessages.push({ 
              name: order.name, 
              cost: order.cost,
              isOwned: true // Assume owned in fallback
            });
          }
          newCounts[id] = count;
        } else {
          missingOrderIds.push({ id, count });
        }
      });
      
      // Fetch missing orders
      if (missingOrderIds.length > 0) {
        console.log('🔵 Fetching', missingOrderIds.length, 'missing orders (fallback)');
        const fetchPromises = missingOrderIds.map(({ id }) =>
          fetch(`${API_URL}/order/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }).then(res => res.ok ? res.json() : null).catch(err => {
            console.error(`Failed to fetch order ${id}:`, err);
            return null;
          })
        );
        
          const fetchedOrders = await Promise.all(fetchPromises);
          
          // Store missing orders for grid display
          const validFetchedOrders = fetchedOrders.filter(o => o !== null);
          setMissingOrders(validFetchedOrders);
          
          // Add fetched orders to sidebar
          fetchedOrders.forEach((order, index) => {
            if (order) {
              const { count } = missingOrderIds[index];
              for (let i = 0; i < count; i++) {
                newMessages.push({ 
                  name: order.name, 
                  cost: order.cost,
                  isOwned: true // Assume owned in fallback
                });
              }
              newCounts[order.id] = count;
            }
          });
        } else {
          // No missing orders, clear the state
          setMissingOrders([]);
        }
        
        // Sort by cost
        newMessages.sort((a, b) => a.cost - b.cost);
        console.log('🔵 Setting sidebar messages (fallback):', newMessages.length);
        setSidebarMessages(newMessages);
        setOrderCounts(newCounts);
      // Store original orders for change detection
      setOriginalStrategyOrders(selected.orders.map(({ orderId, order_id, count }) => ({
        orderId: orderId || order_id,
        count
      })));
      strategyJustLoadedRef.current = true; // Mark that strategy was just loaded (no changes yet)
      strategyLoadedByOnChange.current = false; // Reset flag after loading
    }
  }}
/>

<div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
  <button
    onClick={handleOpenSaveDialog}
    style={{
      padding: "12px 24px",
      fontSize: "1.2em",
      border: "none",
      borderRadius: "8px",
      backgroundColor: "rgba(0,0,0,0.6)",
      color: "white",
      cursor: "pointer",
      transition: "background-color 0.3s ease",
      flex: 1
    }}
  >
    {t('strategy.saveStrategy')}
  </button>

  <button
    style={{
      padding: "12px 24px",
      fontSize: "1.2em",
      border: "none",
      borderRadius: "8px",
      backgroundColor: "rgba(0,0,0,0.6)",
      color: "white",
      cursor: selectedStrategyId ? "pointer" : "not-allowed",
      transition: "background-color 0.3s ease",
      flex: 1,
      opacity: selectedStrategyId ? 1 : 0.5
    }}
    disabled={!selectedStrategyId}
    onClick={() => {
      playSoundEffect('/sounds/click.mp3');
      showConfirmation(t('strategy.alerts.deleteConfirm'), (confirmed) => {
        if (!confirmed) return;

        fetch(`${API_URL}/strategy/${selectedStrategyId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        })
          .then(res => res.json())
          .then(data => {
            showNotification(data.message || t('strategy.alerts.deleted'), 'success');
            setSelectedStrategyId('');
            setOriginalStrategyOrders(null);
            // Refresh strategy list
            setUserStrategies(prev => prev.filter(s => s.id !== parseInt(selectedStrategyId)));
            setSidebarMessages([]);
            setOrderCounts({});
          })
          .catch(err => {
            console.error("Delete failed:", err);
            showNotification(t('strategy.alerts.deleteFailed'), 'error');
          });
      });
    }}
  >
    {t('strategy.deleteStrategy')}
  </button>
</div>

<div style={{ marginTop: "10px", fontWeight: "bold" }}>
  {t('strategy.totalOrders')} {(sidebarMessages || []).length}
</div>

</div>

</div>

 {(sidebarMessages || []).map((msg, index) => {
  const order = (orders || []).find(o => o.name === msg.name && o.cost === msg.cost);
  const isUnowned = msg.isOwned === false;
  const color = order
    ? (combinedFactions[order.faction_name]
        ? factionColors['Combined']
        : factionColors[order.faction_name] || '#FFFFFF')
    : '#FFFFFF';

  return (
    <div
      key={index}
      data-sidebar-order-index={index}
      onMouseEnter={(e) => {
        if (order) {
          const rect = e.currentTarget.getBoundingClientRect();
          setSidebarTooltipRect({
            index,
            top: rect.top,
            bottom: rect.bottom,
            left: e.clientX
          });
        }
      }}
      onMouseLeave={() => {
        setSidebarTooltipRect(null);
      }}
      onClick={() => {
        playSoundEffect('/sounds/remove_order.mp3');
        setSidebarMessages(prev => prev.filter((_, i) => i !== index));
        setOrderCounts(prev => {
          const updated = { ...prev };
          if (order && updated[order.id]) {
            updated[order.id] -= 1;
            if (updated[order.id] <= 0) delete updated[order.id];
          }
          return updated;
        });
        strategyJustLoadedRef.current = false; // User made a change
      }}
      style={{
        border: '0px solid #ccc',
        padding: '5px',
        paddingLeft: isUnowned ? '25px' : '5px',
        marginBottom: '5px',
        borderRadius: '4px',
        backgroundColor: color,
        fontSize: '0.9em',
        cursor: 'pointer',
        position: 'relative',
        filter: isUnowned ? 'grayscale(100%)' : 'none'
      }}
      title={isUnowned ? 'You do not own this order - Click to remove' : t('strategy.clickToRemove')}
    >
      {isUnowned && (
        <span style={{ 
          position: 'absolute',
          left: '5px',
          top: '5px',
          color: 'orange', 
          fontWeight: 'bold',
          filter: 'none',
          zIndex: 1
        }}>⚠️</span>
      )}
      <strong>{msg.name}</strong>: {t('strategy.cost')} {msg.cost}
    </div>
  );
})}

{/* Tooltip for sidebar orders - rendered via portal to avoid clipping. Uses stored rect only (no querySelector in render). */}
{sidebarTooltipRect !== null && createPortal((() => {
  const { index: hoveredSidebarIndex, top: orderItemTop, bottom: orderItemBottom, left: tooltipLeft } = sidebarTooltipRect;
  const msg = (sidebarMessages || [])[hoveredSidebarIndex];
  const order = (orders || []).find(o => o.name === msg?.name && o.cost === msg?.cost);
  const isUnowned = msg?.isOwned === false;
  
  if (!order) return null;
  
  const tooltipTop = orderItemBottom + 5;
  const tooltipWidth = 250 * 0.8;
  const tooltipHeight = 380 * 0.8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let adjustedLeft = tooltipLeft;
  let adjustedTop = tooltipTop;
  let transformOrigin = 'top left';
  
  if (adjustedLeft + tooltipWidth > viewportWidth) {
    adjustedLeft = viewportWidth - tooltipWidth - 10;
  }
  if (adjustedLeft < 10) {
    adjustedLeft = 10;
  }
  if (adjustedTop + tooltipHeight > viewportHeight - 10) {
    const gap = 20;
    const cellsUp = 3;
    const pixelsPerCell = 20;
    const additionalOffset = cellsUp * pixelsPerCell;
    adjustedTop = orderItemTop - tooltipHeight - gap - additionalOffset;
    transformOrigin = 'bottom left';
    if (adjustedTop < 10) {
      adjustedTop = 10;
      transformOrigin = 'top left';
    }
  }
  if (adjustedTop < 10) {
    adjustedTop = 10;
  }
  
  return (
    <div
      style={{
        position: 'fixed',
        left: `${adjustedLeft}px`,
        top: `${adjustedTop}px`,
        zIndex: 99999,
        pointerEvents: 'none', // Don't interfere with mouse events on order items
        transform: 'scale(0.8)',
        transformOrigin: transformOrigin,
        opacity: 1,
        isolation: 'isolate',
        filter: isUnowned ? 'grayscale(100%)' : 'none'
      }}
    >
      <div style={{ opacity: 1, zIndex: 99999 }}>
        <OrderDesign
          order={order}
          count={0}
          minimal={false}
          noMargin={true}
          lazyLoad={false}
          isOOR={false}
        />
      </div>
    </div>
  );
})(), document.body)}

{showTutorialStrategy && createPortal(
  <TutorialMessageBox
    image="/images/tutorial_explain_4.png"
    text={tutorialStrategyStep === 1 ? t('strategy.tutorialText') : t('strategy.tutorialText2')}
    onContinue={async () => {
      if (tutorialStrategyStep === 1) {
        setTutorialStrategyStep(2);
        return;
      }
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${API_URL}/tutorial/mark-seen`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ messageKey: 'strategy' }),
        });
        setSeenTutorialMessages(prev => prev.includes('strategy') ? prev : [...prev, 'strategy']);
      }
      setShowTutorialStrategy(false);
    }}
  />,
  document.body
)}

{showSaveDialog && createPortal(
  <div className={`modal-overlay${isMobile ? ' mobile' : ''}`} style={{ zIndex: 1000000 }} onClick={(e) => {
    // Only close if clicking the overlay itself, not the content
    if (e.target.className.includes('modal-overlay')) {
      setShowSaveDialog(false);
    }
  }}>
    <div className={`modal-content${isMobile ? ' mobile' : ''}`} style={{ zIndex: 1000001, position: 'relative', pointerEvents: 'auto', cursor: 'auto' }}>
      <h3>{t('strategy.modal.saveTitle')}</h3>
      <label>
        {t('strategy.modal.name')}<br />
        <input
          type="text"
          value={modalStrategyName}
          onChange={e => setModalStrategyName(e.target.value)}
          style={{ width: "100%" }}
        />
      </label>
      <br />
      <label>
        {t('strategy.modal.description')}<br />
        <textarea
          value={modalDescription}
          onChange={e => setModalDescription(e.target.value)}
          rows={3}
          style={{ width: "100%", resize: "vertical" }}
        />
      </label>
      <br />
      <label>
        <input
          type="checkbox"
          checked={modalIsPrivate}
          onChange={e => setModalIsPrivate(e.target.checked)}
        />
        {t('strategy.modal.private')}
      </label>
      <br />
      <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
        <button onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          handleSaveStrategy();
        }}>{t('strategy.modal.save')}</button>
        <button onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          setShowSaveDialog(false);
        }}>{t('strategy.modal.cancel')}</button>
      </div>
    </div>
  </div>,
  document.body
)}

{/* Custom Notification and Confirmation Components */}
{NotificationComponent}
{ConfirmationComponent}

</div>
</div>
</div>
</div>
  );
}

export default Strategy;